import DOMPurify from 'dompurify';
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import type { CommentElementProps } from '../../comments/comment-element.component';
import CommentsList from '../../comments/comments-list.component';
import RequiredIndicator from '../../common/required-indicator/required-indicator.component';
import type { DynamicFieldComponentProps } from '../../dynamic-form/dynamic-form.interfaces';
import EmptyFieldLabel from '../../labels/empty-field-label';
import FieldLabel from '../../labels/field-label';
import RichTextArea from '../rich-text-area/rich-text-area.component';

export interface HistoryFieldProps extends DynamicFieldComponentProps<CommentElementProps[]> {
  imageSrc?: string;
  fullName?: string;
}

const removeAllHTMLTags = (value: string): string => {
  return DOMPurify.sanitize(value, { ALLOWED_TAGS: ['#text', 'img'] });
};

export default function HistoryField({
  name,
  label,
  value,
  readOnly,
  required,
  onChange,
  error,
  inputRef,
  labelStyles,
  fullName,
}: HistoryFieldProps) {
  const { t } = useTranslation();

  const commentsRef = useRef<CommentElementProps[]>([]);

  const comments: Array<CommentElementProps> = value ?? [];
  commentsRef.current = comments.filter((comment) => comment.draft !== true);
  const _draftMessage = comments.find((comment) => comment.draft === true)?.message || '';

  const handleChange = (value?: string) => {
    // Handler uses commentsRef to avoid passing stale values when doing updates
    if (onChange && typeof onChange === 'function' && value) {
      let newValue;
      if (removeAllHTMLTags(value).length === 0) {
        newValue = [...commentsRef.current];
      } else {
        newValue = [
          {
            message: value,
            draft: true,
            date: new Date().toISOString(),
            user: fullName || t('user'),
          },
          ...commentsRef.current,
        ];
      }
      onChange?.(newValue as CommentElementProps[]);
    }
  };

  return (
    <>
      {label && (
        <div className="odin-flex">
          <FieldLabel label={label} labelStyles={labelStyles} />
          <RequiredIndicator readOnly={readOnly} required={required} />
        </div>
      )}
      {readOnly && commentsRef.current.length === 0 && <EmptyFieldLabel />}
      {!readOnly && (
        <RichTextArea name={name} value={_draftMessage} onChange={handleChange} inputRef={inputRef} error={error} />
      )}
      {commentsRef.current.length > 0 && <CommentsList comments={commentsRef.current} readOnly={readOnly} />}
    </>
  );
}
